import { useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Typography, makeStyles } from '@material-ui/core';
import Scrollbars from '../scrollbars';
import PermissionsForm from './permissionsForm';
import { useElementMaxHeight } from '../../../utility/hooks';

const useStyles = makeStyles(({ palette: { primary }, spacing }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: 'fit-content',
    maxWidth: '100%',
  },
  scopeLegend: {
    display: 'flex',
    flexWrap: 'nowrap',
    alignItems: 'center',
    gap: spacing(4),
    marginBottom: spacing(6),
    whiteSpace: 'nowrap',
  },
  scopeLegendTitle: {
    color: primary.bluish2,
    flexShrink: 0,
    fontFamily: 'ProximaNova-Bold',
  },
  scopeLegendItem: {
    color: primary.bluish2,
    flexShrink: 0,
  },
  scopeLegendName: {
    fontFamily: 'ProximaNova-Bold',
  },
  scrollViewport: {
    maxHeight: ({ maxHeight }) => maxHeight,
  },
  content: {
    display: 'flex',
    minWidth: 'max-content',
  },
  scrollY: {
    right: -15,
    '& .os-scrollbar-vertical': {
      paddingTop: spacing(18.5),
      paddingBottom: spacing(6),
    },
  },
  scrollX: {
    bottom: -15,
    '& .os-scrollbar-horizontal': {
      paddingLeft: spacing(75),
    },
  },
}));

const PermissionsMatrix = ({
  parentNode = null,
  translations,
  roles,
  permissionGroups,
  permissionsByRole,
  onTogglePermission,
}) => {
  const [expandedGroup, setExpandedGroup] = useState('');
  const [setElementRef, maxHeight] = useElementMaxHeight(parentNode, true);
  const classes = useStyles({ maxHeight });

  const onToggleGroup = useCallback(
    (groupId = '') => setExpandedGroup(groupId),
    []
  );

  return (
    <div ref={setElementRef} className={classes.root}>
      <div className={classes.scopeLegend}>
        <Typography
          className={classes.scopeLegendTitle}
          variant="body1"
        >{`${translations.scopeLegendTitle}:`}</Typography>
        {translations.scopeLegend.map(({ name, description }) => (
          <Typography
            key={name}
            className={classes.scopeLegendItem}
            variant="body2"
          >
            <span className={classes.scopeLegendName}>{name}</span>
            {` - ${description}`}
          </Typography>
        ))}
      </div>
      <Scrollbars
        horizontalScrollClass={classes.scrollX}
        verticalScrollClass={classes.scrollY}
        viewportClass={classes.scrollViewport}
        overlayProps={{
          options: {
            overflow: {
              x: 'scroll',
              y: 'scroll',
            },
          },
        }}
      >
        <div className={classes.content}>
          <PermissionsForm
            translations={translations}
            roles={roles}
            permissionGroups={permissionGroups}
            permissionsByRole={permissionsByRole}
            expandedGroup={expandedGroup}
            onToggleGroup={onToggleGroup}
            onTogglePermission={onTogglePermission}
          />
        </div>
      </Scrollbars>
    </div>
  );
};

PermissionsMatrix.propTypes = {
  parentNode: PropTypes.object,
  translations: PropTypes.object.isRequired,
  roles: PropTypes.arrayOf(PropTypes.object).isRequired,
  permissionGroups: PropTypes.arrayOf(PropTypes.object).isRequired,
  permissionsByRole: PropTypes.object.isRequired,
  onTogglePermission: PropTypes.func.isRequired,
};

export default PermissionsMatrix;
